// Date and time formatting utilities

export const DATE_FORMATS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)', example: '31/12/2024' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)', example: '12/31/2024' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)', example: '2024-12-31' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (31-12-2024)', example: '31-12-2024' },
  { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (31.12.2024)', example: '31.12.2024' },
  { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 31, 2024)', example: 'Dec 31, 2024' },
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY (31 Dec 2024)', example: '31 Dec 2024' },
];

export const TIME_FORMATS = [
  { value: '24h', label: '24-hour (14:30)', example: '14:30' },
  { value: '12h', label: '12-hour (2:30 PM)', example: '2:30 PM' },
];

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a date string according to the specified format
 * @param {string} dateStr - Date string (ISO format or similar)
 * @param {string} format - Format pattern (default: 'DD/MM/YYYY')
 * @returns {string} Formatted date string
 */
export function formatDate(dateStr, format = 'DD/MM/YYYY') {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthShort = MONTHS_SHORT[date.getMonth()];
    
    switch (format) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY':
        return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'DD-MM-YYYY':
        return `${day}-${month}-${year}`;
      case 'DD.MM.YYYY':
        return `${day}.${month}.${year}`;
      case 'MMM DD, YYYY':
        return `${monthShort} ${day}, ${year}`;
      case 'DD MMM YYYY':
        return `${day} ${monthShort} ${year}`;
      default:
        return `${day}/${month}/${year}`;
    }
  } catch (e) {
    return dateStr;
  }
}

/**
 * Format a time string according to the specified format
 * @param {string} timeStr - Time string or Date object
 * @param {string} format - '24h' or '12h'
 * @returns {string} Formatted time string
 */
export function formatTime(timeStr, format = '24h') {
  if (!timeStr) return '-';
  
  try {
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) return timeStr;
    
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    if (format === '12h') {
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes} ${period}`;
    } else {
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
  } catch (e) {
    return timeStr;
  }
}

/**
 * Format a datetime string according to the specified formats
 * @param {string} dateTimeStr - DateTime string (ISO format)
 * @param {string} dateFormat - Date format pattern
 * @param {string} timeFormat - '24h' or '12h'
 * @returns {string} Formatted datetime string
 */
export function formatDateTime(dateTimeStr, dateFormat = 'DD/MM/YYYY', timeFormat = '24h') {
  if (!dateTimeStr) return '-';
  
  const formattedDate = formatDate(dateTimeStr, dateFormat);
  const formattedTime = formatTime(dateTimeStr, timeFormat);
  
  return `${formattedDate} ${formattedTime}`;
}

/**
 * Format month and year from a date string
 * @param {string} dateStr - Date string
 * @param {string} format - Date format to determine order
 * @returns {string} Month/Year string
 */
export function formatMonthYear(dateStr, format = 'DD/MM/YYYY') {
  if (!dateStr) return '-';
  
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      // Try to parse YYYY-MM format
      const parts = dateStr.split('-');
      if (parts.length >= 2) {
        const month = parts[1];
        const year = parts[0];
        if (format.startsWith('MM')) {
          return `${month}/${year}`;
        }
        return `${month}/${year}`;
      }
      return dateStr;
    }
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    if (format.startsWith('MM')) {
      return `${month}/${year}`;
    }
    return `${month}/${year}`;
  } catch (e) {
    return dateStr;
  }
}
